import axios from "axios"
import { catalogPath } from "@/api/v2Paths.js"
import { shareInFlight } from "@/shareInFlight.js"

// Matches the catalog endpoint's own default. Only a safety net: CPricing awaits visitor/fetchVisitor
// before dispatching this, so the visitor's currency is normally already resolved.
const DEFAULT_CURRENCY = "EUR"

const pricing = {
  namespaced: true,
  state: {
    pricingPlans: [],
    // Which currency the Salesforce editions in pricingPlans were fetched for. The legacy endpoint
    // returns every currency at once, but the catalog returns exactly one, so the cache has to be
    // keyed by it — otherwise a USD visitor keeps whatever currency happened to be fetched first
    // and the checkout wizard filters every Salesforce plan out.
    fetchedCurrency: null,
  },
  mutations: {
    ADD_PRICING_PLANS(state, pricingPlans) {
      if (pricingPlans) {
        state.pricingPlans = pricingPlans
      }
    },
    SET_FETCHED_CURRENCY(state, currency) {
      state.fetchedCurrency = currency
    },
  },
  actions: {
    async fetchPricingPlans({ commit, state, rootState }) {
      const currency = rootState.visitor?.visitor?.currencyCode || DEFAULT_CURRENCY
      if (state.pricingPlans.length > 0 && state.fetchedCurrency === currency) {
        return
      }

      await shareInFlight(`pricing:${currency}`, async () => {
        const [legacyRes, sfRes] = await Promise.allSettled([
          axios.get("/public/plans?all_bundles=1"),
          axios.get(catalogPath(currency)),
        ])

        // The two catalogues are merged, not chosen between, and each is handled on its own: a
        // failure on one side must not drop the plans the other side returned successfully.
        let unifiedPlans = []

        if (legacyRes.status === "fulfilled") {
          if (Array.isArray(legacyRes.value.data)) {
            unifiedPlans = [...legacyRes.value.data]
          }
        }

        if (sfRes.status === "fulfilled") {
          const editions = sfRes.value.data?.editions
          if (Array.isArray(editions)) {
            // Tagged so the UI can tell the catalogues apart; legacy plans carry no `source`.
            unifiedPlans = [...unifiedPlans, ...editions.map(plan => ({ ...plan, source: "salesforce" }))]
          }
        }

        commit("ADD_PRICING_PLANS", unifiedPlans)
        commit("SET_FETCHED_CURRENCY", currency)
      })
    },
  },
}

export { pricing }
